import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, Mail, Phone, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DateSectionHeader from '@/components/admin/DateSectionHeader';
import AdminKpiGrid, { AdminKpiTile } from '@/components/admin/AdminKpiGrid';
import AdminRecordCard from '@/components/admin/AdminRecordCard';
import { getServiceStatusLabel } from '@/constants/adminVocabulary';
import { groupItemsByDateSection } from '@/utils/dateSections';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import useAdminListLoupe, { assignLoupeIndexes } from '@/components/admin/useAdminListLoupe';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ServiceRequests() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const listLoupe = useAdminListLoupe(`${searchTerm}|${statusFilter}`);


  const fetchServiceRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/service-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServiceRequests(response.data);
      setFilteredRequests(response.data);
    } catch (error) {
      toast.error('Failed to load service requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchServiceRequests();
    }
  }, [isAuthenticated, token, fetchServiceRequests]);

  useEffect(() => {
    const query = searchTerm.toLowerCase();
    const filtered = serviceRequests.filter((req) => {
      if (statusFilter !== 'all' && req.status !== statusFilter) return false;
      if (!query) return true;
      return (
        (req.name || '').toLowerCase().includes(query) ||
        (req.email || '').toLowerCase().includes(query) ||
        (req.phone || '').toLowerCase().includes(query) ||
        (req.details?.vin || '').toLowerCase().includes(query)
      );
    });
    setFilteredRequests(filtered);
  }, [searchTerm, statusFilter, serviceRequests]);

  const groupedRequests = useMemo(
    () => groupItemsByDateSection(filteredRequests, (request) => request.created_at),
    [filteredRequests],
  );
  const requestLoupeIndexes = assignLoupeIndexes(groupedRequests);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      scheduled: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      completed: 'bg-green-500/10 text-green-400 border-green-500/30',
    };

    return (
      <span className={`px-3 py-1 text-xs font-['Manrope'] border ${styles[status] || styles.pending}`}>
        {getServiceStatusLabel(status)}
      </span>
    );
  };

  const formatDate = (dateString) => formatDateTimeMdY(dateString);

  const handleViewDetails = (request) => {
    navigate(`/admin/service-requests/${request.id}`);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="service-requests-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
          Service Requests
        </h1>
        <p className="text-sm font-['Manrope'] text-white/40 mt-1">Review and manage service appointments</p>
      </div>

      <div>
        <AdminKpiGrid>
          {[
            { key: 'all', label: 'Total Requests', color: 'text-white' },
            { key: 'pending', label: getServiceStatusLabel('pending'), color: 'text-yellow-400' },
            { key: 'scheduled', label: getServiceStatusLabel('scheduled'), color: 'text-blue-400' },
            { key: 'completed', label: getServiceStatusLabel('completed'), color: 'text-green-400' },
          ].map((tile) => (
            <AdminKpiTile
              key={tile.key}
              label={tile.label}
              value={tile.key === 'all'
                ? serviceRequests.length
                : serviceRequests.filter((request) => request.status === tile.key).length}
              color={tile.color}
              active={tile.key === 'all' ? statusFilter === 'all' : statusFilter === tile.key}
              testId={`stat-service-${tile.key}`}
              onClick={() => {
                if (tile.key === 'all') {
                  setStatusFilter('all');
                  return;
                }
                setStatusFilter((current) => (current === tile.key ? 'all' : tile.key));
              }}
            />
          ))}
        </AdminKpiGrid>

        {/* Search */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or VIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
        </div>

        {/* Requests Table */}
        <div className="admin-list-card bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/60 font-['Manrope']">
              Loading service requests...
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-12 text-center text-white/60 font-['Manrope']">
              {serviceRequests.length === 0 ? (
                <p>No service requests yet.</p>
              ) : (
                <>
                  <p>No service requests match your filters.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                    className="mt-4 min-h-11 px-4 border border-white/15 text-white text-sm font-['Manrope'] hover:bg-white/5"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
            <div className="lg:hidden space-y-3 p-3">
              {groupedRequests.map((section) => (
                <React.Fragment key={section.key}>
                  <DateSectionHeader variant="list" label={section.label} count={section.items.length} />
                  {section.items.map((request) => (
                    <AdminRecordCard
                      key={request.id}
                      title={request.name}
                      subtitle={request.email}
                      badge={getStatusBadge(request.status)}
                      fields={[
                        {
                          label: 'Vehicle',
                          value: [request.details?.vehicleYear, request.details?.vehicleMake, request.details?.vehicleModel].filter(Boolean).join(' ') || '—',
                        },
                        { label: 'Phone', value: request.phone || '—' },
                        { label: 'Date', value: formatDate(request.created_at) },
                      ]}
                      onClick={() => handleViewDetails(request)}
                      primary={
                        <button
                          type="button"
                          onClick={() => handleViewDetails(request)}
                          className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                        >
                          Open
                        </button>
                      }
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="admin-list-scroll overflow-x-auto hidden lg:block">
              <div className="admin-list-cols-service bg-white/5 border-b border-white/10">
                {['Customer', 'Contact', 'Vehicle', 'Status', 'Date'].map((h) => (
                  <div key={h} className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                    {h}
                  </div>
                ))}
                <div className="px-6 py-4 text-right text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                  Actions
                </div>
              </div>
              <div
                className={`admin-list-loupe ${listLoupe.active ? 'is-loupe' : ''}`}
                onMouseLeave={listLoupe.clear}
              >
                {groupedRequests.map((section) => (
                  <React.Fragment key={section.key}>
                    <DateSectionHeader variant="list" label={section.label} count={section.items.length} />
                    {section.items.map((request) => (
                  <div
                    key={request.id}
                    onClick={() => handleViewDetails(request)}
                    onMouseEnter={() => listLoupe.focus(requestLoupeIndexes.get(request))}
                    className={`admin-list-cols-service admin-list-magnify border-b border-white/10 hover:bg-white/5 cursor-pointer ${listLoupe.classFor(requestLoupeIndexes.get(request))}`}
                  >
                    <div className="px-6 py-4">
                      <div className="font-['Manrope'] font-semibold text-white">
                        {request.name}
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <div className="space-y-1">
                        <a
                          href={`mailto:${request.email}`}
                          className="flex items-center gap-2 min-w-0 text-sm font-['Manrope'] text-blue-400 hover:text-blue-300"
                        >
                          <Mail className="w-3 h-3 shrink-0" />
                          <span className="min-w-0">{request.email}</span>
                        </a>
                        {request.phone && (
                          <a
                            href={`tel:${request.phone}`}
                            className="flex items-center gap-2 min-w-0 text-sm font-['Manrope'] text-green-400 hover:text-green-300"
                          >
                            <Phone className="w-3 h-3 shrink-0" />
                            <span className="min-w-0">{request.phone}</span>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <div className="text-white font-['Manrope']">
                        {request.details.vehicleYear} {request.details.vehicleMake} {request.details.vehicleModel}
                      </div>
                      <div className="text-xs text-white/60 font-['Manrope'] font-mono">
                        {request.details.vin}
                      </div>
                    </div>
                    <div className="px-6 py-4">{getStatusBadge(request.status)}</div>
                    <div className="px-6 py-4">
                      <div className="flex items-center gap-2 text-white/60 font-['Manrope'] text-sm">
                        <Calendar className="w-4 h-4 shrink-0" />
                        {formatDate(request.created_at)}
                      </div>
                    </div>
                    <div className="admin-list-actions px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleViewDetails(request); }}
                          className="p-2 hover:bg-white/10 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-blue-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            </>
          )}
        </div>
      </div>


    </div>
  );
}
